import { defineStore } from 'pinia'

import { cultureSubsidyApi } from '@/views/culture-subsidy/api'
import {
  AdminSection,
  BeneficiaryCumul,
  CaretakerSection,
  CatalogProduct,
  CultureSubsidySettings,
  EnrichedProduct,
  OperationPayload,
  PocketBalances,
  ReserveMovement,
  SettingsPayload,
  SimulateInput,
  SpecialOperation,
  SubsidyAlert,
  SubsidyComputation,
  SupervisedCaretaker,
  TransferPayload
} from '@/views/culture-subsidy/types'
import { fallbackCapacity, resolveApplicableRule } from '@/views/culture-subsidy/utils/calculator'

interface State {
  loading: boolean
  saving: boolean
  caretakerSection: CaretakerSection
  adminSection: AdminSection
  settings: CultureSubsidySettings | null
  pockets: PocketBalances | null
  products: CatalogProduct[]
  operations: SpecialOperation[]
  movements: ReserveMovement[]
  beneficiaries: BeneficiaryCumul[]
  alerts: SubsidyAlert[]
  supervised: SupervisedCaretaker[]
  selectedAdminCaretakerId: string | null
  simulation: SubsidyComputation | null
}

const emptyPockets = (): PocketBalances => ({
  free: 0,
  loyalty: 0,
  recycling: 0,
  cultureReserve: 0
})

export const useCultureSubsidyStore = defineStore('cultureSubsidy', {
  state: (): State => ({
    loading: false,
    saving: false,
    caretakerSection: 'overview',
    adminSection: 'supervision',
    settings: null,
    pockets: null,
    products: [],
    operations: [],
    movements: [],
    beneficiaries: [],
    alerts: [],
    supervised: [],
    selectedAdminCaretakerId: null,
    simulation: null
  }),
  getters: {
    fallbackCapacityAmount(state): number {
      if (!state.settings || !state.pockets) return 0
      return fallbackCapacity(state.settings, state.pockets)
    },
    enrichedProducts(state): EnrichedProduct[] {
      if (!state.settings) return []
      return state.products.map(product => {
        const rule = resolveApplicableRule(product, state.settings as CultureSubsidySettings, state.operations)
        return {
          ...product,
          appliedRate: rule.rate,
          appliedValue: rule.value,
          ruleSource: rule.source,
          specialOperationName: rule.operationName
        }
      })
    },
    currentPockets(state): PocketBalances {
      return state.pockets || emptyPockets()
    }
  },
  actions: {
    applySnapshot(snapshot: {
      settings: CultureSubsidySettings
      pockets: PocketBalances
      products: CatalogProduct[]
      operations: SpecialOperation[]
      movements: ReserveMovement[]
      beneficiaries: BeneficiaryCumul[]
      alerts: SubsidyAlert[]
    }, keepAlerts = false) {
      this.settings = snapshot.settings
      this.pockets = snapshot.pockets
      this.products = snapshot.products
      this.operations = snapshot.operations
      this.movements = snapshot.movements
      this.beneficiaries = snapshot.beneficiaries
      if (!keepAlerts) this.alerts = snapshot.alerts
    },
    async loadCaretaker(caretakerId?: string, keepAlerts = false) {
      this.loading = true
      try {
        const snapshot = await cultureSubsidyApi.getCaretakerSnapshot(caretakerId)
        this.applySnapshot(snapshot, keepAlerts)
      } finally {
        this.loading = false
      }
    },
    async loadAdmin() {
      this.loading = true
      try {
        this.supervised = await cultureSubsidyApi.listSupervisedCaretakers()
        this.alerts = await cultureSubsidyApi.listAlerts()
        const caretakerId = this.selectedAdminCaretakerId || this.supervised[0]?.id
        this.selectedAdminCaretakerId = caretakerId || null
        const snapshot = await cultureSubsidyApi.getCaretakerSnapshot(caretakerId)
        this.applySnapshot(snapshot, true)
      } finally {
        this.loading = false
      }
    },
    async selectAdminCaretaker(caretakerId: string) {
      this.selectedAdminCaretakerId = caretakerId
      this.saving = true
      try {
        const snapshot = await cultureSubsidyApi.getCaretakerSnapshot(caretakerId)
        this.applySnapshot(snapshot, true)
      } finally {
        this.saving = false
      }
    },
    async saveSettings(payload: SettingsPayload, caretakerId?: string) {
      this.saving = true
      try {
        const snapshot = await cultureSubsidyApi.updateSettings(payload, caretakerId)
        this.applySnapshot(snapshot)
      } finally {
        this.saving = false
      }
    },
    async transfer(kind: 'free' | 'loyalty' | 'recycling' | 'wire' | 'empty', payload: TransferPayload, caretakerId?: string) {
      this.saving = true
      try {
        const methods = {
          free: cultureSubsidyApi.transferFromFree,
          loyalty: cultureSubsidyApi.transferFromLoyalty,
          recycling: cultureSubsidyApi.transferFromRecycling,
          wire: cultureSubsidyApi.registerWire,
          empty: cultureSubsidyApi.emptyReserve
        }
        const snapshot = await methods[kind](payload, caretakerId)
        this.applySnapshot(snapshot)
        this.supervised = await cultureSubsidyApi.listSupervisedCaretakers()
      } finally {
        this.saving = false
      }
    },
    async simulate(payload: SimulateInput, caretakerId?: string) {
      this.simulation = await cultureSubsidyApi.simulatePurchase(payload, caretakerId)
      return this.simulation
    },
    async updateProduct(productId: string, patch: Partial<Pick<CatalogProduct, 'eligible' | 'valuePerProduct'>>) {
      this.saving = true
      try {
        this.products = await cultureSubsidyApi.updateProduct(productId, patch)
      } finally {
        this.saving = false
      }
    },
    async createOperation(payload: OperationPayload, caretakerId?: string) {
      this.saving = true
      try {
        await cultureSubsidyApi.createOperation(payload, caretakerId)
        await this.loadCaretaker(caretakerId, true)
        this.supervised = await cultureSubsidyApi.listSupervisedCaretakers()
      } finally {
        this.saving = false
      }
    },
    async deleteOperation(operationId: string, caretakerId?: string) {
      this.saving = true
      try {
        await cultureSubsidyApi.deleteOperation(operationId)
        await this.loadCaretaker(caretakerId, true)
      } finally {
        this.saving = false
      }
    },
    async resetDemo() {
      this.loading = true
      try {
        await cultureSubsidyApi.resetDemo()
        await this.loadCaretaker()
        this.supervised = await cultureSubsidyApi.listSupervisedCaretakers()
        this.alerts = await cultureSubsidyApi.listAlerts()
      } finally {
        this.loading = false
      }
    }
  }
})
